"use client";

import React, { useState } from "react";
import { Camera, Activity, Pipette, Layers, Wrench, ShieldCheck, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, ArrowRight, PhoneCall, Clock, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
export default function SewerMethods() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("inspection");
  const inspectionSteps = [{
    step: "01",
    title: "Cleanout Access & Insertion",
    desc: "Our technicians access your main sewer line through an exterior ground cleanout or interior cleanout hub using a self-leveling fiber-optic reel."
  }, {
    step: "02",
    title: "Real-Time 4K Video Telemetry",
    desc: "The camera transmits high-definition live footage with an integrated digital sonde transmitter measuring precise depth and linear distance."
  }, {
    step: "03",
    title: "Defect Mapping & Documentation",
    desc: "We pinpoint structural offsets, root penetrations, bellies, channel corrosion, or collapsed sidewalls with exact GPS and surface-level markers."
  }, {
    step: "04",
    title: "Customized Engineering Solution",
    desc: "Based on video findings, we determine whether non-invasive trenchless restoration (CIPP) or targeted spot excavation is the most durable solution."
  }];
  const repairMethods = [{
    id: "hydro-jetting",
    icon: Zap,
    badge: "Non-Invasive Scouring",
    title: "Hydro Jetting & Root Descaling",
    description: "High-pressure water blasted up to 4,000 PSI at 18 GPM sweeps out grease, mineral scale, and heavy root intrusions without pipe excavation.",
    bestFor: "Tree root blockages, grease sludge, scale accumulation",
    longevity: "Preventative (1-3 Years)",
    impact: "Zero lawn or structural disruption"
  }, {
    id: "cipp-lining",
    icon: Pipette,
    badge: "Trenchless Technology",
    title: "CIPP Trenchless Epoxy Lining",
    description: "An epoxy-saturated felt tube is inserted into the damaged host pipe, inflated, and cured into a seamless, joint-free structural liner within existing pipes.",
    bestFor: "Cracked pipes, separated joints, root-infiltrated lines",
    longevity: "50+ Year Design Life",
    impact: "1-2 small access points only"
  }, {
    id: "pipe-bursting",
    icon: Layers,
    badge: "Full Line Replacement",
    title: "Hydraulic Pipe Bursting",
    description: "A hardened conical bursting head breaks apart the fractured clay or cast iron host pipe while pulling a new seamless HDPE pipe directly in its place.",
    bestFor: "Collapsed lines, undersized mains, heavily deformed conduits",
    longevity: "100+ Year Material Lifespan",
    impact: "Small launch and receiving pits"
  }, {
    id: "open-trench",
    icon: Wrench,
    badge: "Deep Structural Fix",
    title: "Open-Cut Excavation & Replacement",
    description: "Precision trenching performed for severely back-pitched sewer lines (bellies), severe utility cross-bores, or catastrophic ground shifts.",
    bestFor: "Severe pipe bellies, unaligned grades, shallow accessible runs",
    longevity: "50+ Years (PVC Schedule 40)",
    impact: "Trench excavation along pipe path"
  }];
  const comparisonData = [{
    method: "High-Definition Video Camera",
    phase: "Diagnostic",
    disruption: "null",
    duration: "45-90 Mins",
    lifespan: "Immediate Diagnosis"
  }, {
    method: "Hydro Jetting Cleanout",
    phase: "Maintenance",
    disruption: "null",
    duration: "1-2 Hours",
    lifespan: "12-36 Months"
  }, {
    method: "CIPP Epoxy Pipe Relining",
    phase: "Rehabilitation",
    disruption: "Minimal (No Trench)",
    duration: "4-8 Hours",
    lifespan: "50+ Years"
  }, {
    method: "Trenchless Pipe Bursting",
    phase: "Replacement",
    disruption: "Low (Pits Only)",
    duration: "1 Day",
    lifespan: "100 Years (HDPE)"
  }, {
    method: "Traditional Open Excavation",
    phase: "Replacement",
    disruption: "Moderate to High",
    duration: "1-3 Days",
    lifespan: "50+ Years"
  }];
  const technicalFaqs = [{
    q: "How does a sewer camera locate exact pipe blockages underneath concrete slabs?",
    a: "The sewer camera head contains an electromagnetic sonde transmitter operating at 512 Hz. Above ground, our technician uses a digital locator receiver to track the signal, identifying the precise depth, direction, and point of failure to within inches without breaking concrete unnecessarily."
  }, {
    q: "Can trenchless CIPP lining repair crushed or collapsed pipes?",
    a: "CIPP lining requires a host pipe with at least 60-70% structural integrity to guide the bladder. If the pipe is completely crushed or has a severe belly, hydraulic pipe bursting or localized spot excavation is utilized instead."
  }, {
    q: "Is hydro jetting safe for older clay and cast iron sewer lines?",
    a: "Yes, provided a sewer camera inspection is conducted first. We inspect the wall thickness and joint condition to dial in exact PSI tolerances, ensuring we strip away grease and scale without compromising fragile legacy pipe walls."
  }, {
    q: "What causes recurring sewer backups in Jonesboro homes?",
    a: "The most common local culprits are aggressive pine and oak tree root intrusions seeking moisture through clay pipe joints, ground settlement creating pipe bellies (standing water traps), and accumulated cooking grease solidified inside older cast iron lines."
  }];
  return <section id="sewerinspectionrepairmethods" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-medium border-primary/20 text-primary bg-primary/5">
            <Activity className="size-3.5 text-accent mr-1.5" />
            Advanced Diagnostic & Repair Standards
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Sewer Line Inspection & Precision Repair Methods
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Eliminate guesswork before digging. We deploy fiber-optic diagnostic
            imaging and trenchless restorative engineering to solve sewer line
            failures throughout Jonesboro and surrounding areas.
          </p>
        </div>

        {/* Tab Navigation for Technical Views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-14">
          <div className="flex justify-center mb-8">
            <TabsList className="h-11 p-1 bg-muted rounded-lg w-full max-w-md grid grid-cols-2">
              <TabsTrigger value="inspection" className="text-sm font-semibold transition-all">
                <Camera className="size-4 mr-2" />
                Video Inspection Process
              </TabsTrigger>
              <TabsTrigger value="repairs" className="text-sm font-semibold transition-all">
                <Wrench className="size-4 mr-2" />
                Repair & Replacement
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: Inspection Technical Walkthrough */}
          <TabsContent value="inspection" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Left Column: Visual & Spec Box */}
              <div className="lg:col-span-5 space-y-6">
                <Card className="border border-border bg-card shadow-sm overflow-hidden">
                  <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_camera_inspection.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_camera_inspection.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_camera_inspection.webp-wvc-sizes" alt="Professional sewer line video camera inspection in action" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2.5 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 shadow-sm">
                      <Camera className="size-3.5 text-accent" />
                      Live HD Video Sonde
                    </div>
                  </div>
                  <CardHeader className="p-5 pb-3">
                    <CardTitle className="text-lg font-bold font-default">
                      Diagnostic Technical Specs
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground">
                      Continuous footage recording with linear distance counter
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-5 pt-0 space-y-3 text-sm">
                    <div className="flex justify-between items-center py-2 border-b border-border/60">
                      <span className="text-muted-foreground">Camera Optics</span>
                      <span className="font-semibold text-foreground">
                        Self-Leveling 4K CMOS Sensor
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/60">
                      <span className="text-muted-foreground">Sonde Frequency</span>
                      <span className="font-semibold text-foreground">
                        512 Hz Multi-Directional
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/60">
                      <span className="text-muted-foreground">Pipe Diameter Range</span>
                      <span className="font-semibold text-foreground">
                        2-inch to 12-inch Mains
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Inspection Report</span>
                      <span className="font-semibold text-foreground">
                        Digital Video + Defect Log
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Process Steps */}
              <div className="lg:col-span-7 space-y-4">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold font-default text-foreground">
                    How We Inspect Underground Lines
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    Complete optical analysis identifies cracks, bellies, and joint separations before any excavation begins.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {inspectionSteps.map((step, i) => <motion.div key={step.step} data-index={i} initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 15
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.35,
                  delay: i * 0.08
                }}>
                      <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                        <CardHeader className="p-5 pb-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-mono font-bold text-accent bg-accent/10 px-2.5 py-1 rounded-md">
                              STEP {step.step}
                            </span>
                            <CheckCircle2 className="size-4 text-chart-4" />
                          </div>
                          <CardTitle className="text-base font-bold font-default text-foreground">
                            {step.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-5 pt-0 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {step.desc}
                        </CardContent>
                      </Card>
                    </motion.div>)}
                </div>

                <div className="pt-3">
                  <Card className="border border-accent/30 bg-secondary/50 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="size-5 text-accent shrink-0 mt-0.5" />
                      <div className="text-xs sm:text-sm">
                        <span className="font-semibold text-foreground">
                          Emergency Warning Signs:
                        </span>{" "}
                        <span className="text-muted-foreground">
                          Gurgling toilets, foul sewer odor in yard, multiple slow drains, or soggy patches near the cleanout indicate an active main line blockage requiring rapid inspection.
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TAB 2: Repair Technologies Grid */}
          <TabsContent value="repairs" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {repairMethods.map((method, i) => {
              const MethodIcon = method.icon;
              return <motion.div key={method.id} data-index={i} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.35,
                delay: i * 0.08
              }}>
                    <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-all flex flex-col justify-between">
                      <CardHeader className="p-6 pb-3">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                            <MethodIcon className="size-6" />
                          </div>
                          <Badge variant="secondary" className="font-medium text-xs text-secondary-foreground">
                            {method.badge}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl font-bold font-default text-foreground">
                          {method.title}
                        </CardTitle>
                        <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          {method.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-6 pt-0 space-y-2.5">
                        <Separator className="my-2" />
                        <div className="text-xs grid grid-cols-1 gap-1.5">
                          <div>
                            <span className="font-semibold text-foreground">
                              Target Issues:
                            </span>{" "}
                            <span className="text-muted-foreground">
                              {method.bestFor}
                            </span>
                          </div>
                          <div>
                            <span className="font-semibold text-foreground">
                              Estimated Durability:
                            </span>{" "}
                            <span className="text-muted-foreground">
                              {method.longevity}
                            </span>
                          </div>
                          <div>
                            <span className="font-semibold text-foreground">
                              Surface Impact:
                            </span>{" "}
                            <span className="text-muted-foreground">
                              {method.impact}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Technical Comparison Table Section */}
        <div className="my-16">
          <div className="mb-6">
            <h3 className="text-2xl font-bold font-default text-foreground">
              Method Comparison Matrix
            </h3>
            <p className="text-sm text-muted-foreground">
              Technical review of timelines, structural durability, and surface disruption across all sewer procedures.
            </p>
          </div>

          <Card className="border border-border bg-card shadow-xs overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/60">
                <TableRow>
                  <TableHead className="font-semibold text-foreground">
                    Procedure / Method
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    Classification
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    Yard / Property Disruption
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    Avg. Execution Time
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    Expected Longevity
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, i) => <TableRow key={row.method} data-index={i}>
                    <TableCell className="font-medium text-foreground">
                      {row.method}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {row.phase}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.disruption}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.duration}
                    </TableCell>
                    <TableCell className="font-semibold text-foreground">
                      {row.lifespan}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Technical FAQs & Callout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <h3 className="text-2xl font-bold font-default text-foreground mb-4">
              Frequently Asked Technical Questions
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {technicalFaqs.map((faq, i) => <AccordionItem key={faq.q} value={`faq-${i}`} data-index={i}>
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Right Column: Emergency Service Dispatch Card */}
          <div className="lg:col-span-5">
            <Card className="border border-primary/20 bg-primary text-primary-foreground shadow-md relative overflow-hidden">
              <CardHeader className="p-6 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="size-5 text-accent" />
                  <span className="text-xs uppercase tracking-wider font-semibold text-accent">
                    Jonesboro Emergency Plumber
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-primary-foreground">
                  Immediate Sewer Line Support in Jonesboro
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 text-sm mt-1">
                  Experiencing an urgent main line backup or sewage overflow? Our certified technicians are on call 24 hours a day with mobile camera diagnostic units.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-2 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90">
                    <Clock className="size-4 text-accent shrink-0" />
                    <span>24/7 Rapid Emergency Dispatch</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>Upfront Flat-Rate Estimates Before Work Begins</span>
                  </div>
                </div>

                <Separator className="bg-primary-foreground/20 my-2" />

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <Button asChild variant="default" size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                    <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>(470) 847-9076</span>
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-primary-foreground/10 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/20">
                    <Link to="/contact/" className="flex items-center justify-center gap-2">
                      <span>Book Inspection</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}